/**
 * Owner rule C45 (TASK-0230, INC-11a): a back-dated delivery asks, per line, whether to add stock
 * that a physical count already saw.
 *
 * One section, shared by every screen that records a delivery (the PO Receive tab, Record
 * invoice, invoice import review, and later reconcile-into-PO): the lines the server asked
 * about, each with the day it arrived, when the item was last counted and how, and an
 * "Add to stock" tick that starts unticked. Unticked is the owner's default: the order line,
 * its cost and the bill are still recorded, only the stock is left as counted.
 */
import { useQuery } from '@tanstack/react-query';
import { ClipboardList } from 'lucide-react';
import { ApiError, api } from '@/lib/api/client';
import type { CountCheckResponse, CountQuestion, CountSource } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import { countLabel, time } from '@/lib/format';
import { Callout } from '@/ui/Callout';
import { Checkbox } from '@/ui/Field';
import { useDebouncedValue } from '@/ui/MultiSelect';

export const COUNT_CONFIRMATION_REQUIRED = 'COUNT_CONFIRMATION_REQUIRED';
export const DELIVERY_DATE_REQUIRED = 'DELIVERY_DATE_REQUIRED';
export const CHANGED_SINCE_OPENED = 'CHANGED_SINCE_OPENED';

/** How the count was taken, in the words staff use. */
export const COUNT_SOURCE_LABEL: Record<CountSource, string> = {
  phone: 'phone',
  sheet: 'count sheet',
  'first-count': 'first count',
  'bulk-import': 'count import',
  console: 'console count',
};

const dayFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

/**
 * `Oct 12` for a store day `YYYY-MM-DD`. Built as a local calendar day: `new Date('2026-10-12')`
 * is UTC midnight, which is still Oct 11 in the store's zone.
 */
export function storeDayLabel(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return day;
  return dayFormat.format(
    new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  );
}

/** `Oct 14, 9:05 PM · phone` */
export function lastCountedLabel(
  question: Pick<CountQuestion, 'lastCountedOn' | 'lastCountedAt' | 'countSource'>
): string {
  return `${storeDayLabel(question.lastCountedOn)}, ${time(question.lastCountedAt)} · ${
    COUNT_SOURCE_LABEL[question.countSource] ?? question.countSource
  }`;
}

/** The ConfirmDialog consequence for lines left unticked; null when every asked line adds stock. */
export function countConfirmationConsequence(leftOut: number): string | null {
  if (leftOut <= 0) return null;
  return `${countLabel(leftOut, 'line')} ${leftOut === 1 ? 'records' : 'record'} cost and billing only; stock stays as counted.`;
}

/** Today in the browser (the store's own zone), `YYYY-MM-DD`. */
export function localToday(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Calendar arithmetic on a `YYYY-MM-DD` day. */
export function addDays(day: string, days: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return localToday(new Date(y!, m! - 1, d! + days));
}

/** Typed delivery dates go back at most this many days (the API refuses older ones). */
export const DELIVERY_DATE_WINDOW_DAYS = 60;

/** The questions a 409 COUNT_CONFIRMATION_REQUIRED carried, else null. */
export function countQuestionsFromError(error: unknown): CountQuestion[] | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  if (error.body?.code !== COUNT_CONFIRMATION_REQUIRED) return null;
  const details = error.body.details as { questions?: CountQuestion[] } | undefined;
  return Array.isArray(details?.questions) ? details.questions : [];
}

/** The refusal code on an API error, if any. */
export function refusalCode(error: unknown): string | null {
  return error instanceof ApiError ? (error.body?.code ?? null) : null;
}

/** What `useCountCheck` tells a screen about the delivery it shows right now. */
export interface CountCheckState {
  /**
   * The questions for exactly the date and products on screen; empty while `pending`, for a
   * delivery dated today and for no products. Never an earlier input's answer.
   */
  questions: CountQuestion[];
  /**
   * True while the answer for the inputs on screen is not in yet: during the debounce after a
   * change, and while the request runs. Every submit waits for false (TASK-0230 review F2): a
   * question that arrives after the confirm opened would otherwise be answered by default.
   */
  pending: boolean;
}

/**
 * Ask the server which of these products were counted on or after `receiptDate`. Debounced, and
 * idle for a delivery dated today (today never asks) or with no products. The server decides again
 * at submit, under its locks; this only lets the screen ask first.
 */
export function useCountCheck(
  receiptDate: string,
  productIds: number[],
  today: string
): CountCheckState {
  const ids = [...new Set(productIds)].sort((a, b) => a - b);
  const current = `${receiptDate}|${ids.join(',')}`;
  const key = useDebouncedValue(current, 300);
  const [date, csv] = key.split('|') as [string, string];
  const debouncedIds = csv ? csv.split(',').map(Number) : [];
  const query = useQuery({
    queryKey: ['purchasing', 'count-check', date, csv],
    queryFn: () =>
      api.post<CountCheckResponse>('/purchasing/count-check', {
        receiptDate: date,
        productIds: debouncedIds,
      }),
    enabled: date < today && debouncedIds.length > 0,
    staleTime: 15_000,
  });
  const needed = receiptDate < today && ids.length > 0;
  const forScreen = key === current;
  // A failed check does not hold the screen: the server asks at submit and its 409 re-renders.
  const settled = forScreen && !query.isFetching && (query.isSuccess || query.isError);
  return {
    questions: needed && forScreen && query.data ? query.data.questions : [],
    pending: needed && !settled,
  };
}

/**
 * The key an operator's answer is stored under: the screen's line plus the question as it was
 * shown (product, delivery day, the count it is about). Re-matching a line to another product,
 * changing the date or a newer count gives a new key, so an answer never carries over to a
 * question the operator did not see (TASK-0230 review F1, F2).
 */
export function countAnswerKey(
  lineKey: string,
  question: Pick<CountQuestion, 'productId' | 'receiptDate' | 'lastCountedAt'>
): string {
  return `${lineKey}|${question.productId}|${question.receiptDate}|${question.lastCountedAt}`;
}

/** Thrown, before any request, when a submit is attempted while a question is still open. */
export class CountQuestionOpenError extends Error {}

/**
 * The answers to send, one per asked line. Throws `CountQuestionOpenError`, so nothing is sent,
 * while the check for the inputs on screen is still running or any line is unanswered: an
 * unanswered line is never sent as "leave stock out" (D-38).
 */
export function answersToSend<T>(
  lines: T[],
  answerOf: (line: T) => boolean | null,
  pending: boolean
): { line: T; addStock: boolean }[] {
  if (pending) {
    throw new CountQuestionOpenError(
      'Still checking which items were counted after this delivery. Nothing was sent; try again in a moment.'
    );
  }
  const answered: { line: T; addStock: boolean }[] = [];
  let open = 0;
  for (const line of lines) {
    const addStock = answerOf(line);
    if (addStock === null) open += 1;
    else answered.push({ line, addStock });
  }
  if (open > 0) {
    throw new CountQuestionOpenError(`${unansweredMessage(open)} Nothing was sent.`);
  }
  return answered;
}

export interface CountConfirmationLine {
  /** The caller's key for the line (a PO item id, an import line index…). */
  key: string;
  name: string;
  receiptDate: string;
  lastCountedAt: string;
  lastCountedOn: string;
  countSource: CountSource;
  /** Delivered the day the item was counted: no default, the operator must pick (D-38). */
  sameDayAsCount: boolean;
}

/**
 * The answer a screen sends for one asked line. A line delivered before the count's day defaults
 * to leaving stock out (absent = false); a line delivered on the count's own day has no default
 * (absent = unanswered, `null`).
 */
export function stockAnswer(
  line: Pick<CountConfirmationLine, 'key' | 'sameDayAsCount'>,
  decisions: Record<string, boolean>
): boolean | null {
  const answer = decisions[line.key];
  if (answer !== undefined) return answer;
  return line.sameDayAsCount ? null : false;
}

/** Lines the operator still has to answer (same-day lines with no choice); submit waits for 0. */
export function unansweredLines<
  T extends Pick<CountConfirmationLine, 'key' | 'sameDayAsCount'>,
>(lines: T[], decisions: Record<string, boolean>): T[] {
  return lines.filter((line) => stockAnswer(line, decisions) === null);
}

/** The sentence that keeps a submit disabled while same-day lines are unanswered. */
export function unansweredMessage(count: number): string | null {
  if (count <= 0) return null;
  return `Answer ${countLabel(count, 'line')} delivered the day ${count === 1 ? 'its item was' : 'their items were'} counted.`;
}

/** Two choices for a line delivered the day its item was counted; neither starts selected. */
export function SameDayChoice({
  line,
  answer,
  onChange,
}: {
  line: Pick<CountConfirmationLine, 'name'>;
  answer: boolean | null;
  onChange: (addStock: boolean) => void;
}) {
  const option = (value: boolean, label: string) => (
    <button
      type="button"
      role="radio"
      aria-checked={answer === value}
      onClick={() => onChange(value)}
      className={cn(
        'text-2xs rounded-xs border px-1.5 py-0.5 whitespace-nowrap transition-colors duration-100',
        answer === value
          ? 'border-signal bg-signal text-ink-inverse'
          : 'border-line-strong bg-surface-sunken text-ink hover:border-ink-faint'
      )}
    >
      {label}
    </button>
  );
  return (
    <div
      role="radiogroup"
      aria-label={`Stock for ${line.name}`}
      className="flex flex-wrap items-center gap-1"
    >
      {option(true, 'Add to stock')}
      {option(false, 'Already in the count')}
    </div>
  );
}

/** The hint under a same-day line. */
export function sameDayHint(line: Pick<CountConfirmationLine, 'lastCountedAt'>): string {
  return `Delivered the day this item was counted (counted at ${time(line.lastCountedAt).toLowerCase()}). Was it on the shelf when it was counted?`;
}

export function CountConfirmation({
  lines,
  decisions,
  onChange,
  className,
}: {
  lines: CountConfirmationLine[];
  /**
   * key → answer. A missing key is "leave stock out" for a line delivered before the count's day
   * (the default) and unanswered for a line delivered on the count's own day.
   */
  decisions: Record<string, boolean>;
  onChange: (key: string, addStock: boolean) => void;
  className?: string;
}) {
  if (lines.length === 0) return null;
  return (
    <Callout
      tone="warn"
      icon={<ClipboardList />}
      title="Counted after this delivery"
      className={cn('space-y-2', className)}
    >
      <table className="mt-1 w-full text-xs" aria-label="Counted after this delivery">
        <thead>
          <tr className="text-2xs text-ink-faint tracking-wider uppercase">
            <th className="py-1 pr-2 text-left font-medium">Item</th>
            <th className="w-20 px-2 py-1 text-left font-medium">Arrived</th>
            <th className="w-44 px-2 py-1 text-left font-medium">Last counted</th>
            <th className="w-28 py-1 pl-2 text-left font-medium">Add to stock</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.key} className="border-warn/20 border-t align-top">
              <td className="text-ink py-1 pr-2">
                {line.name}
                {line.sameDayAsCount ? (
                  <div className="text-2xs text-ink-muted mt-0.5">
                    {sameDayHint(line)}
                  </div>
                ) : null}
              </td>
              <td className="px-2 py-1 font-mono tabular-nums">
                {storeDayLabel(line.receiptDate)}
              </td>
              <td className="px-2 py-1">{lastCountedLabel(line)}</td>
              <td className="py-1 pl-2">
                {line.sameDayAsCount ? (
                  <SameDayChoice
                    line={line}
                    answer={stockAnswer(line, decisions)}
                    onChange={(addStock) => onChange(line.key, addStock)}
                  />
                ) : (
                  <Checkbox
                    checked={decisions[line.key] === true}
                    onCheckedChange={(checked) => onChange(line.key, checked)}
                    label={<span className="sr-only">Add {line.name} to stock</span>}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-ink-muted mt-2">
        Unticked lines record the quantity, cost and bill on the order without adding
        stock again, because the count already includes them.
      </p>
    </Callout>
  );
}
